import moment from 'moment';
import { useHistory } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import React, { useState, useEffect } from 'react';
import { CircularProgress } from "@material-ui/core";
import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalBody, ModalHeader, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { TextField, FormControl, Select, MenuItem, Menu, Checkbox, ListItemText, Popover, Dialog } from '@mui/material';

import './index.css';
import BoxView from './BoxView';
import constants from '../../constants';
import 'react-toastify/dist/ReactToastify.css';
import EmptyAgentState from './EmptyAgentState';
import TitleRoute from '../../components/titleRoute.js';
import ViewStateManager from '../../utils/viewStateManager';
import DefaultAvatar from '../../static/img/agent-img-3.png';
import { getWorkspacePath } from '../../utils/workspaceUtils';
import TagsManager from '../../components/Tags/TagsManager.js';
import TableAnimationFiles from '../../components/Animations/TableAnimationFiles.js';
import { getAllAgents, deleteAgent, setNewAgentModal, cloneAgent, } from '../../store/Agent';
import notFoundImg from '../../static/img/not-found.png';

const Agents = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  // Load saved view state
  const savedViewState = ViewStateManager.getAgentView();
  const [isTableView, setTableView] = useState(savedViewState['isTableView']);

  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [statusDropdownAnchor, setStatusDropdownAnchor] = useState(null);
  const [tagFilterAnchor, setTagFilterAnchor] = useState(null);
  const [selectedValues, setSelectedValues] = useState({});
  const [selectedTagsList, setSelectedTagsList] = useState([]); // For TagsManager filter mode
  const [agentTypes, setAgentTypes] = useState([]);
  const [agentStatuses, setAgentStatuses] = useState([]);
  const [isTagsModal, setTagsModal] = useState(false);
  const { allAgents, newAgentModal, isLoading } = useSelector(state => state['Agent']);
  const { currentWorkspace } = useSelector(state => state['Settings']);
  const { agents: agentsState } = useSelector(state => state['Objects']);
  const allTags = agentsState?.allTags || [];

  const [tagsManagerOpen, setTagsManagerOpen] = useState(false);

  // Save view state when it changes
  useEffect(() => { ViewStateManager.setAgentView({ isTableView }) }, [isTableView]);
  const handleClick = (event) => setStatusDropdownAnchor(event.currentTarget);
  const handleClose = () => setStatusDropdownAnchor(null);
  const handleClickEditTags = () => {
    setTagsModal(true);
  };

  const handleOpenTagsManager = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget); // jis button pe click hua usi ko anchor bana diya
  };

  const handleCloseTagsManager = () => {
    setAnchorEl(null);
  };

  const toggleValue = (value) => setSelectedValues((prev) => ({ ...prev, [value]: !prev[value] }));
  const handleClickTagFilter = (event) => setTagFilterAnchor(event.currentTarget);
  const handleCloseTagFilter = () => setTagFilterAnchor(null);
  const handleTagsFilterChange = (selectedTagNames) => {
    setSelectedTagsList(selectedTagNames);
  };

  const [isDelete, setDelete] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  useEffect(() => {
    dispatch(getAllAgents());
  }, []);

  // Extract distinct agent types and statuses when agents data changes
  useEffect(() => {
    if (allAgents && allAgents.length > 0) {
      // Extract types
      const types = [...new Set(allAgents.map(agent => agent['type']).filter(type => type))];
      setAgentTypes(types.sort());

      // Compute and extract statuses
      const statuses = [...new Set(allAgents.map(agent => {
        return agent['callStats']?.['totalCalls'] > 0 ? 'active' : 'inactive';
      }))];
      setAgentStatuses(statuses.sort());
    }
  }, [allAgents]);

  // Filter agents based on search and type
  useEffect(() => {
    let filtered = allAgents || [];

    // Apply search filter with null checks
    if (searchText && searchText.trim()) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(agent => {
        if (!agent) return false;
        return (
          agent['name']?.toLowerCase().includes(search) ||
          agent['script']?.toLowerCase().includes(search) ||
          agent['scriptSummary']?.toLowerCase().includes(search) ||
          agent['phone']?.toLowerCase().includes(search) ||
          (agent['tags'] && Array.isArray(agent['tags']) && agent['tags'].some(tag => {
            const tagName = typeof tag === 'string' ? tag : tag?.name;
            return tagName && tagName.toLowerCase().includes(search);
          }))
        );
      });
    }

    // Apply type filter with null check
    if (selectedType && selectedType !== 'all') {
      filtered = filtered.filter(agent => agent && agent['type'] === selectedType);
    }

    // Apply status filter with null checks
    const selectedStatusList = selectedValues ? Object.keys(selectedValues).filter(key => selectedValues[key]) : [];
    if (selectedStatusList.length > 0) {
      filtered = filtered.filter(agent => {
        if (!agent) return false;
        const status = agent['callStats']?.['totalCalls'] > 0 ? 'active' : 'inactive';
        return selectedStatusList.includes(status);
      });
    }

    // Apply tag filter with proper null checks
    if (selectedTagsList && selectedTagsList.length > 0) {
      filtered = filtered.filter(agent => {
        if (!agent || !agent['tags'] || !Array.isArray(agent['tags']) || agent['tags'].length === 0) {
          return false;
        }
        return selectedTagsList.some(selectedTag => {
          if (!selectedTag) return false;
          return agent['tags'].some(agentTag => {
            if (!agentTag) return false;
            const tagName = typeof agentTag === 'string' ? agentTag : agentTag?.name;
            return tagName === selectedTag;
          });
        });
      });
    }

    setFilteredContacts(filtered);
  }, [searchText, selectedType, allAgents, selectedValues, selectedTagsList]);

  const toggleNewAgentModal = () => dispatch(setNewAgentModal(!newAgentModal));

  const dataAgents = filteredContacts?.map((item, index) => {
    const handleRowClick = () => {
      document.body.classList.add("hide-layout");
      history.push(getWorkspacePath(`/agents/${item['_id']}`, currentWorkspace?._id)); // save state
    };
    // Compute status based on call activity
    const status = item['callStats']?.['totalCalls'] > 0 ? 'active' : 'inactive';
    return {
      _id: item['_id'],
      agentName: (
        <div className='agent-name-box' onClick={handleRowClick}>
          <div className='img-box'>
            <img src={item?.image || DefaultAvatar} alt="Agent" className='agent-avatar' />
            <span className={`status-agent ${status === 'active' ? 'green' : 'grey'}`}></span>
          </div>
          <h4>{item['name'] || 'N/A'}</h4>
          <div className='content-area' onClick={(e) => e.stopPropagation()}>
            <Dropdown className="create-btn dropdwon-email" isOpen={openDropdownId === index} toggle={() => setOpenDropdownId(openDropdownId === index ? null : index)}>
              <DropdownToggle>
                <svg width="20px" height="20px" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="#4b5563" class="bi bi-three-dots-vertical">
                  <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
                </svg>
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={() => dispatch(cloneAgent(item['_id']))}>Clone</DropdownItem>
                <DropdownItem onClick={() => setDelete(item['_id'])}>Delete</DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
      ),
      agentType: <div className='agent-type' onClick={handleRowClick}> {item['type'] || 'N/A'} </div>,
      agentTask: <div className='agent-type' onClick={handleRowClick}> {item['callStats']?.['totalCalls'] || 0} Calls </div>,
      phoneAgent: (
        <div className='agent-type' onClick={handleRowClick} style={{ display: 'flex', flexDirection: 'column', gap: '5px', padding: '6px 0' }}>
          {item['inboundPhone'] && (
            <div style={{ backgroundColor: '#E8F4FD', color: '#1E48B4', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '500', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', width: 'fit-content' }}><span style={{ marginRight: '5px', fontSize: '14px' }}>↓</span>{item['inboundPhone']}</div>
          )}
          {item['outboundPhone'] && (
            <div style={{ backgroundColor: '#FEF3E2', color: '#D97706', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '500', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', width: 'fit-content' }}><span style={{ marginRight: '5px', fontSize: '14px' }}>↑</span>{item['outboundPhone']}</div>
          )}
          {!item['inboundPhone'] && !item['outboundPhone'] && <span style={{ color: '#999' }}>N/A</span>}
        </div>
      ),
      voiceAgent: (<div className='voice-box' onClick={handleRowClick}> <span className='img-box'> <img src={item['voiceId']?.['voiceImg']} alt="Voice Icon" /> </span> {item['voiceId']?.['voiceName'] || 'N/A'} </div>),
      toolsAgent: (
        <div className='tools-box' onClick={handleRowClick}>
          <ul>
            <li><a href='https://google.com/' target="_blank" rel="noopener noreferrer"><svg width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.9694 6.11766C10.9694 5.7506 10.9365 5.39766 10.8753 5.05884H6V7.0612H8.78589C8.66589 7.70826 8.30118 8.25649 7.75295 8.62355V9.92238H9.42589C10.4047 9.0212 10.9694 7.69414 10.9694 6.11766Z" fill="#4285F4" /><path fill-rule="evenodd" clip-rule="evenodd" d="M5.99967 11.1766C7.39732 11.1766 8.56909 10.7131 9.42556 9.92247L7.75262 8.62365C7.28909 8.93423 6.69614 9.11776 5.99967 9.11776C4.65143 9.11776 3.51025 8.20717 3.10319 6.98364H1.37378V8.32482C2.22555 10.0166 3.97614 11.1766 5.99967 11.1766Z" fill="#34A853" /><path fill-rule="evenodd" clip-rule="evenodd" d="M3.10349 6.98354C2.99996 6.67295 2.94114 6.34118 2.94114 6C2.94114 5.65883 2.99996 5.32706 3.10349 5.01647V3.67529H1.37408C1.02349 4.37412 0.823486 5.16471 0.823486 6C0.823486 6.8353 1.02349 7.62589 1.37408 8.32472L3.10349 6.98354Z" fill="#FBBC05" /><path fill-rule="evenodd" clip-rule="evenodd" d="M5.99967 2.88231C6.75967 2.88231 7.44203 3.14349 7.9785 3.65643L9.46321 2.17172C8.56674 1.33643 7.39497 0.823486 5.99967 0.823486C3.97614 0.823486 2.22555 1.98349 1.37378 3.67526L3.10319 5.01644C3.51025 3.7929 4.65143 2.88231 5.99967 2.88231Z" fill="#EA4335" /></svg></a></li>
            <li><a href='https://gmail.com/' target="_blank" rel="noopener noreferrer"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_2063_5275)"><path d="M1.41189 11.8555H3.47072V6.85554L0.529541 4.64966V10.9732C0.529541 11.4614 0.925129 11.8555 1.41189 11.8555Z" fill="#4285F4" /><path d="M10.5295 11.8555H12.5884C13.0766 11.8555 13.4707 11.46 13.4707 10.9732V4.64966L10.5295 6.85554" fill="#34A853" /><path d="M10.5295 3.03207V6.8556L13.4707 4.64972V3.47325C13.4707 2.38207 12.2251 1.76001 11.3531 2.41443" fill="#FBBC04" /><path d="M3.4707 6.85551V3.03198L7.00012 5.67904L10.5295 3.03198V6.85551L7.00012 9.50257" fill="#EA4335" /><path d="M0.529541 3.47325V4.64972L3.47072 6.8556V3.03207L2.64719 2.41443C1.77366 1.76001 0.529541 2.38207 0.529541 3.47325" fill="#C5221F" /></g><defs><clipPath id="clip0_2063_5275"><rect width="12.9412" height="12.9412" fill="white" transform="translate(0.529541 0.529541)" /></clipPath></defs></svg></a></li>
            <li><a href='https://hubspot.com/' target="_blank" rel="noopener noreferrer"><svg width="9" height="11" viewBox="0 0 9 11" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.27245 7.18027L3.75805 7.07002C3.76819 7.06772 3.78535 7.0639 3.80564 7.05739C3.93976 7.02209 4.05469 6.93707 4.12622 6.82025C4.19774 6.70342 4.22026 6.56391 4.18905 6.4311L4.18709 6.42268C4.17104 6.35905 4.143 6.29893 4.10441 6.24543C4.05003 6.17769 3.98273 6.121 3.90626 6.07852C3.81624 6.02818 3.72204 5.98541 3.62466 5.95066L3.09225 5.75964C2.79348 5.65054 2.49471 5.54449 2.19282 5.44075C1.99741 5.37261 1.83165 5.31251 1.68772 5.26848C1.66033 5.26104 1.63314 5.25287 1.6062 5.24398C1.43224 5.19192 1.30977 5.1701 1.20641 5.16933C1.13733 5.16681 1.06852 5.17909 1.00476 5.20532C0.938151 5.23362 0.87835 5.27536 0.829243 5.32782C0.804881 5.35497 0.781967 5.38333 0.760595 5.41281C0.719042 5.47546 0.684766 5.54249 0.658406 5.61264C0.559491 5.89938 0.510185 6.20035 0.512531 6.50307C0.514481 6.7764 0.521892 7.12744 0.675178 7.36556C0.712231 7.42681 0.761767 7.47964 0.820663 7.5206C0.929874 7.59448 1.03986 7.60443 1.15493 7.61247C1.32654 7.62434 1.49231 7.58338 1.65808 7.54548L3.27128 7.17989L3.27245 7.18027ZM8.6901 4.65023C8.55755 4.37715 8.3793 4.1278 8.16277 3.91254C8.13479 3.88517 8.1048 3.85983 8.07306 3.83675C8.04373 3.81516 8.01327 3.79509 7.98179 3.77664C7.94938 3.75922 7.91605 3.7435 7.88194 3.72956C7.8146 3.70357 7.74243 3.69183 7.67015 3.6951C7.60111 3.69893 7.53364 3.71807 7.4724 3.74985C7.37996 3.79502 7.27972 3.86775 7.14633 3.98911C7.12799 4.0071 7.10498 4.02739 7.08392 4.04653C6.97432 4.14798 6.85184 4.27316 6.70636 4.41863C6.48131 4.64181 6.26015 4.86614 6.03978 5.09239L5.64584 5.49358C5.57391 5.56675 5.50841 5.64577 5.45004 5.72978C5.40007 5.80087 5.3647 5.88084 5.3459 5.96521C5.33535 6.03003 5.33694 6.09619 5.35058 6.16045L5.35253 6.16887C5.38366 6.30154 5.46603 6.41717 5.58229 6.49143C5.69854 6.56569 5.83962 6.59278 5.97582 6.567C5.99221 6.56473 6.00848 6.56167 6.02457 6.55781L8.12377 6.08159C8.28953 6.04407 8.45725 6.00962 8.60585 5.92463C8.7057 5.86836 8.80048 5.81208 8.86562 5.69877C8.90032 5.63636 8.92131 5.5675 8.92725 5.49664C8.95962 5.21565 8.81023 4.89677 8.6901 4.65023ZM4.93207 5.51655C5.08419 5.32897 5.08419 5.04951 5.09745 4.82059C5.14347 4.05687 5.19145 3.29315 5.23006 2.52904C5.24449 2.23963 5.27609 1.95405 5.25853 1.66234C5.2441 1.42193 5.24254 1.14554 5.0877 0.948006C4.81467 0.599642 4.23195 0.628354 3.8345 0.682714C3.71189 0.699336 3.59019 0.721821 3.46981 0.75009C3.3489 0.778418 3.22877 0.809426 3.11176 0.84656C2.7303 0.969061 2.19399 1.19416 2.1035 1.62559C2.05202 1.86945 2.17371 2.11866 2.26732 2.34146C2.3816 2.61096 2.53723 2.85405 2.6792 3.10786C3.05481 3.77818 3.43705 4.44466 3.81812 5.11153C3.9324 5.31059 4.05604 5.56249 4.27641 5.66585C4.29124 5.67197 4.30606 5.67733 4.32127 5.68231C4.41995 5.71906 4.52799 5.72595 4.63057 5.7026L4.6489 5.69877C4.74407 5.67312 4.82988 5.62221 4.89736 5.55215C4.90984 5.54067 4.92076 5.52918 4.93207 5.51655ZM4.74992 7.56118C4.69055 7.47921 4.60629 7.41778 4.50919 7.38566C4.41209 7.35355 4.30713 7.35241 4.20933 7.3824C4.18632 7.38984 4.1639 7.39892 4.14224 7.40958C4.10872 7.42636 4.07675 7.44596 4.04668 7.46815C3.96082 7.53456 3.88412 7.61164 3.81851 7.69746C3.80173 7.71851 3.78613 7.74646 3.76585 7.76483L3.42808 8.22039C3.23696 8.47611 3.04779 8.73221 2.86096 8.99215C2.73849 9.1602 2.63318 9.30223 2.54932 9.42741C2.53372 9.45115 2.51734 9.47718 2.50251 9.49861C2.40227 9.65021 2.34572 9.76123 2.31646 9.85999C2.29461 9.92561 2.28769 9.99513 2.29618 10.0637C2.30554 10.1349 2.33012 10.2034 2.36795 10.265C2.38823 10.2956 2.41007 10.3255 2.43348 10.3542C2.48402 10.4113 2.54094 10.4627 2.60314 10.5073C2.83639 10.6666 3.09147 10.7811 3.35982 10.8695C3.58331 10.9422 3.81539 10.9859 4.05019 10.9989C4.09036 11.0008 4.13054 11 4.17032 10.9966C4.20721 10.9932 4.24392 10.9881 4.28031 10.9813C4.31673 10.9729 4.35266 10.9627 4.38796 10.9506C4.4566 10.9254 4.51906 10.8863 4.57128 10.8358C4.62082 10.7872 4.65904 10.7286 4.68284 10.6639C4.72184 10.5682 4.74758 10.4472 4.76474 10.2673C4.76591 10.2417 4.76981 10.211 4.77254 10.1831C4.7858 10.0338 4.79205 9.8577 4.80141 9.65174C4.81818 9.33439 4.83105 9.01856 4.84119 8.70197L4.86303 8.13846C4.8681 8.00907 4.86381 7.86513 4.82715 7.73612C4.81103 7.67415 4.78496 7.6151 4.74992 7.56118ZM8.56178 8.4428C8.49157 8.36662 8.39172 8.29121 8.23414 8.1978C8.21152 8.18517 8.18461 8.16871 8.16004 8.15416C8.02899 8.07645 7.87102 7.99529 7.68653 7.89691C7.40297 7.74378 7.11941 7.59525 6.83273 7.44748L6.32685 7.1841C6.30072 7.17644 6.2742 7.15807 6.24923 7.1462C6.15196 7.09743 6.04865 7.06128 5.94188 7.03863C5.9051 7.03166 5.86778 7.02782 5.83033 7.02715C5.80608 7.02702 5.78185 7.02843 5.75778 7.03136C5.65651 7.04671 5.56276 7.09306 5.48992 7.1638C5.41708 7.23453 5.36886 7.32605 5.35214 7.42528C5.34473 7.48844 5.34707 7.55237 5.35955 7.61439C5.38413 7.74684 5.44419 7.87738 5.5066 7.99185L5.77651 8.48913C5.92706 8.76935 6.07918 9.04804 6.23519 9.32596C6.33582 9.50704 6.41968 9.66208 6.49808 9.79071C6.5129 9.81482 6.52967 9.84085 6.54254 9.86344C6.6381 10.0177 6.71455 10.115 6.79217 10.185C6.84387 10.2338 6.90593 10.2708 6.9739 10.2933C7.04187 10.3158 7.11407 10.3233 7.18533 10.3152C7.22245 10.3107 7.25929 10.3043 7.29571 10.296C7.37032 10.2764 7.44243 10.2485 7.51062 10.213C7.71592 10.1 7.90516 9.96099 8.07345 9.79951C8.27549 9.60427 8.45413 9.39143 8.59298 9.14795C8.61249 9.1135 8.62887 9.0779 8.64291 9.04077C8.65598 9.00676 8.6673 8.97214 8.67684 8.93702C8.68503 8.90104 8.69127 8.86505 8.69556 8.82868C8.70256 8.75721 8.69419 8.68509 8.67099 8.61699C8.64809 8.55163 8.6108 8.49241 8.56178 8.4428Z" fill="#FF1A1A" /></svg></a></li>
            <li><a href='https://linkedin.com/' target="_blank" rel="noopener noreferrer"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.1529 11.1764H0.952896V4.2529H3.1529V11.1764ZM2.0529 3.28231C1.34113 3.28231 0.823486 2.76466 0.823486 2.0529C0.823486 1.34113 1.40584 0.823486 2.0529 0.823486C2.76466 0.823486 3.28231 1.34113 3.28231 2.0529C3.28231 2.76466 2.76466 3.28231 2.0529 3.28231ZM11.1764 11.1764H8.97643V7.42349C8.97643 6.32349 8.52348 5.99996 7.87642 5.99996C7.22936 5.99996 6.58231 6.5176 6.58231 7.48819V11.1764H4.38231V4.2529H6.4529V5.22349C6.64701 4.77055 7.42348 4.05878 8.52348 4.05878C9.75289 4.05878 11.047 4.77055 11.047 6.90584V11.1764H11.1764Z" fill="#2867B2" /></svg></a></li>
          </ul>
          <span>+2</span>
        </div>
      ),
      updatedAt: moment(item['updatedAt']).format('lll'),
      agentTags: (() => {
        if (!item['tags'] || !Array.isArray(item['tags']) || item['tags'].length === 0) {
          return <span style={{ color: '#999' }}>No tags</span>;
        }

        const validTags = item['tags'].filter(tag => tag && (typeof tag === 'string' || tag?.name));
        if (validTags.length === 0) {
          return <span style={{ color: '#999' }}>No tags</span>;
        }

        const firstTag = validTags[0];
        const firstTagName = typeof firstTag === 'string' ? firstTag : firstTag?.name;
        const firstTagColor = typeof firstTag === 'string' ? '#6B7280' : (firstTag?.color || '#6B7280');
        const remainingCount = validTags.length - 1;

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span
              style={{
                backgroundColor: `${firstTagColor}20`, // 20% opacity for background
                color: firstTagColor,
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                display: 'inline-block',
                border: `1px solid ${firstTagColor}40`, // 40% opacity for border
                whiteSpace: 'nowrap'
              }}
            >
              {firstTagName}
            </span>
            {remainingCount > 0 && (
              <span
                style={{
                  color: '#000000',
                  fontSize: '12px',
                  display: 'inline-block',
                  whiteSpace: 'nowrap'
                }}
              >
                +{remainingCount}
              </span>
            )}
          </div>
        );
      })(),
      agentImprovement: (() => {
        const suggestion = item['improvementSuggestions']?.find(s => s['priority'] === 'high') ||
          item['improvementSuggestions']?.[0] ||
          { message: 'Performing well', priority: 'low' };

        const iconColor = suggestion['priority'] === 'high' ? '#dc3545' :
          suggestion['priority'] === 'medium' ? '#ffc107' : '#28a745';

        return (
          <div
            onClick={handleRowClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px'
            }}
          >
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="4" cy="4" r="4" fill={iconColor} />
            </svg>
            {suggestion['message']}
          </div>
        );
      })(),
      rowClick: (history) => () => history.push(getWorkspacePath(`/agents/${item['_id']}`, currentWorkspace?._id)),
    };
  }) || [];


  // set selected rows
  const handleSelectedRowsChange = ({ selectedRows }) => { setSelectedRows(selectedRows); };

  // handle checkbox toggle for box view
  const handleBoxViewSelect = (agentId) => {
    const agent = filteredContacts.find(a => a['_id'] === agentId);
    if (!agent) return;

    const isSelected = selectedRows.some(row => row['_id'] === agentId);
    if (isSelected) setSelectedRows(selectedRows.filter(row => row['_id'] !== agentId));
    else setSelectedRows([...selectedRows, agent]);
  };

  // table stats
  const isPaginated = dataAgents.length > 100;
  const headerScrollHeight = isPaginated ? 'calc(100vh - 189px)' : 'calc(100vh - 161px)';
  const responsiveClass = isPaginated ? 'datatable--paginated' : 'datatable--fullheight contact-table';

  // Check if there are no agents at all (not just filtered out)
  const hasNoAgents = !isLoading && allAgents && allAgents.length === 0;

  return (
    <>
      <div className='content'>
        <div className='main-container'>
          <div className='bottom-content-area'>
            <div className='tabs-content'>
              <div className='content'>
                <div className='allemails-area'>
                  <div className='page-title-area'>
                    <div>
                      <TitleRoute />
                      <p className='records-title'>{filteredContacts.length} records</p>
                    </div>
                    <div className='right-btn-area'>
                      {dataAgents.length > 0 &&
                        <div className='right-btn-area'>
                          <button className='btn-style-new' onClick={toggleNewAgentModal}> + Create Agent</button>
                        </div>
                      }
                    </div>
                  </div>
                  {hasNoAgents ? <EmptyAgentState /> :
                    <div className='row'>
                      <div className='col-12'>
                        <div className="table-container filters-table">
                          {selectedRows.length > 0 ? (
                            <div className='header-table-filters advanced-table-filters style-filter-show'>
                              <div className='left-area'>
                                <p>{selectedRows.length} agent{selectedRows.length > 1 ? 's' : ''} selected</p>
                                {selectedRows.length <= 1 &&
                                  <>
                                    <div className="filter-deals-owner">
                                      <button className='assing-btn' onClick={() => { dispatch(cloneAgent(selectedRows[0]['_id'])); setSelectedRows([]); }}>
                                        <i className='icon'>
                                          <svg width="9" height="10" viewBox="0 0 9 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5.125 1.71875H1.375C1.11612 1.71875 0.90625 1.92862 0.90625 2.1875V5.9375C0.90625 6.19638 1.11612 6.40625 1.375 6.40625H2V7.1875H1.375C0.684644 7.1875 0.125 6.62786 0.125 5.9375V2.1875C0.125 1.49714 0.684644 0.9375 1.375 0.9375H5.125C5.81536 0.9375 6.375 1.49714 6.375 2.1875V2.5H5.59375V2.1875C5.59375 1.92862 5.38388 1.71875 5.125 1.71875Z" fill="var(--primary-color)" />
                                            <path fill-rule="evenodd" clip-rule="evenodd" d="M2.625 4.375C2.625 3.68464 3.18464 3.125 3.875 3.125H7.625C8.31536 3.125 8.875 3.68464 8.875 4.375V8.125C8.875 8.81536 8.31536 9.375 7.625 9.375H3.875C3.18464 9.375 2.625 8.81536 2.625 8.125V4.375ZM3.875 3.90625H7.625C7.88388 3.90625 8.09375 4.11612 8.09375 4.375V8.125C8.09375 8.38388 7.88388 8.59375 7.625 8.59375H3.875C3.61612 8.59375 3.40625 8.38388 3.40625 8.125V4.375C3.40625 4.11612 3.61612 3.90625 3.875 3.90625Z" fill="var(--primary-color)" />
                                          </svg>
                                        </i>
                                        Clone
                                      </button>
                                    </div>
                                    <div className="filter-deals-owner">
                                      <button onClick={() => setDelete(selectedRows[0]['_id'])} className='assing-btn'>
                                        <i className='icon'>
                                          <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M9.69189 2.74089C8.16564 2.58964 6.63023 2.51172 5.09939 2.51172C4.19189 2.51172 3.28439 2.55755 2.37689 2.64922L1.44189 2.74089" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                                            <path d="M3.96289 2.27775L4.06372 1.67734C4.13706 1.24192 4.19206 0.916504 4.96664 0.916504H6.16747C6.94206 0.916504 7.00164 1.26025 7.07039 1.68192L7.17122 2.27775" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                                            <path d="M8.70641 4.18945L8.4085 8.80487C8.35808 9.52445 8.31683 10.0836 7.03808 10.0836H4.09558C2.81683 10.0836 2.77558 9.52445 2.72516 8.80487L2.42725 4.18945" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                                            <path d="M4.80176 7.5625H6.32801" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                                            <path d="M4.4209 5.729H6.71257" stroke="#4B5563" stroke-width="0.6875" stroke-linecap="round" stroke-linejoin="round" />
                                          </svg>
                                        </i>
                                        Delete
                                      </button>
                                    </div>
                                  </>
                                }
                                <div className="filter-deals-owner">
                                  <button className="assing-btn" onClick={handleOpenTagsManager}>
                                    <i className='icon'>
                                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M5.0292 9.15828C4.4417 9.15828 3.85004 8.93328 3.4042 8.48745L1.5167 6.59995C1.05837 6.14162 0.816703 5.50828 0.84587 4.86245L0.94587 2.77912C0.991703 1.78328 1.7792 0.995784 2.7792 0.945784L4.86254 0.845784C5.50837 0.820784 6.1417 1.05828 6.60004 1.51662L8.48754 3.40412C9.38337 4.29995 9.38337 5.76245 8.48754 6.65828L6.65837 8.48745C6.20837 8.93328 5.62087 9.15828 5.0292 9.15828ZM1.95837 6.15412L3.84587 8.04162C4.16254 8.35828 4.58337 8.53328 5.0292 8.53328C5.47504 8.53328 5.89587 8.35828 6.21254 8.04162L8.0417 6.21245C8.35837 5.89578 8.53337 5.47495 8.53337 5.02912C8.53337 4.58328 8.35837 4.16245 8.0417 3.84578L6.1542 1.95828C5.82087 1.62495 5.35837 1.44578 4.8917 1.47078L2.80837 1.57078C2.1292 1.59995 1.60004 2.12912 1.5667 2.80412L1.4667 4.88745C1.44587 5.35828 1.62504 5.82078 1.95837 6.15412Z" fill="var(--primary-color)" />
                                        <path d="M3.95829 5.31258C3.21246 5.31258 2.60413 4.70425 2.60413 3.95841C2.60413 3.21258 3.21246 2.60425 3.95829 2.60425C4.70413 2.60425 5.31246 3.21258 5.31246 3.95841C5.31246 4.70425 4.70413 5.31258 3.95829 5.31258ZM3.95829 3.22925C3.55829 3.22925 3.22913 3.55841 3.22913 3.95841C3.22913 4.35841 3.55829 4.68758 3.95829 4.68758C4.35829 4.68758 4.68746 4.35841 4.68746 3.95841C4.68746 3.55841 4.35829 3.22925 3.95829 3.22925Z" fill="var(--primary-color)" />
                                      </svg>
                                    </i>
                                    Add tags
                                  </button>
                                  <Menu anchorEl={anchorEl}
                                    open={Boolean(anchorEl && anchorEl.tagName)}
                                    onClose={handleCloseTagsManager}
                                    anchorOrigin={{
                                      vertical: 'bottom',
                                      horizontal: 'left',
                                    }}
                                    transformOrigin={{
                                      vertical: 'top',
                                      horizontal: 'left',
                                    }}
                                  >
                                    <TagsManager
                                      objectType="agents"
                                      mode="select"
                                      selectedObjects={selectedRows.map(row => row._id || row)}
                                      onClose={handleCloseTagsManager}
                                      onTagsApplied={() => {
                                        setAnchorEl(null);
                                        setTagsManagerOpen(false);
                                        setSelectedRows([]);
                                        // Refresh agents data
                                        dispatch(getAllAgents());
                                      }}
                                    />
                                  </Menu>
                                </div>
                              </div>
                              <div className='right-side-filter'>
                                <button className='close' onClick={() => { setToggleCleared(prev => !prev); setSelectedRows([]); }}>
                                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <mask id="path-1-inside-1_1030_1913" fill="white">
                                      <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" />
                                    </mask>
                                    <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" fill="var(--primary-color)" />
                                    <path d="M8.21204 0.303865L7.94019 0.0318008L7.94012 0.0318703L8.21204 0.303865ZM9.69251 0.303865L9.96446 0.0319116L9.96443 0.0318845L9.69251 0.303865ZM6.47962 4.9982L6.20766 4.72625L5.93571 4.9982L6.20766 5.27015L6.47962 4.9982ZM9.69251 9.69254L9.96444 9.96451L9.96446 9.96449L9.69251 9.69254ZM8.95227 10.0002V10.3848H8.95235L8.95227 10.0002ZM8.21204 9.69254L7.94008 9.96449L7.94018 9.96459L8.21204 9.69254ZM4.99817 6.47867L5.27012 6.20672L4.99813 5.93472L4.72617 6.20676L4.99817 6.47867ZM1.78528 9.69254L1.51312 9.42046L1.50411 9.43012L1.78528 9.69254ZM1.04504 10.0002V10.3848H1.04522L1.04504 10.0002ZM0.303834 9.69254L0.0318532 9.96446L0.0318803 9.96449L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L0.303834 8.21109ZM3.51672 4.9982L3.78868 5.27015L4.06063 4.9982L3.78868 4.72625L3.51672 4.9982ZM0.303834 0.303865L0.0318803 0.0319116L0.0318571 0.0319349L0.303834 0.303865ZM4.99817 3.51676L4.72622 3.78871L4.99813 4.06062L5.27008 3.78875L4.99817 3.51676ZM8.21204 0.303865L8.48388 0.575929C8.73895 0.321065 9.16579 0.321107 9.42058 0.575846L9.69251 0.303865L9.96443 0.0318845C9.4091 -0.523338 8.49544 -0.522995 7.94019 0.0318008L8.21204 0.303865ZM9.69251 0.303865L9.42055 0.575819C9.67551 0.830776 9.67551 1.2584 9.42055 1.51336L9.69251 1.78531L9.96446 2.05726C10.5198 1.50191 10.5198 0.587261 9.96446 0.0319116L9.69251 0.303865ZM9.69251 1.78531L9.42055 1.51336L6.20766 4.72625L6.47962 4.9982L6.75157 5.27015L9.96446 2.05726L9.69251 1.78531ZM6.47962 4.9982L6.20766 5.27015L9.42055 8.48305L9.69251 8.21109L9.96446 7.93914L6.75157 4.72625L6.47962 4.9982ZM9.69251 8.21109L9.42055 8.48305C9.67551 8.738 9.67551 9.16563 9.42055 9.42058L9.69251 9.69254L9.96446 9.96449C10.5198 9.40914 10.5198 8.49449 9.96446 7.93914L9.69251 8.21109ZM9.69251 9.69254L9.42057 9.42056C9.28679 9.55432 9.12078 9.61552 8.95219 9.61555L8.95227 10.0002L8.95235 10.3848C9.31445 10.3847 9.67923 10.2497 9.96444 9.96451L9.69251 9.69254ZM8.95227 10.0002V9.61555C8.7839 9.61555 8.61783 9.55432 8.48389 9.42048L8.21204 9.69254L7.94018 9.96459C8.22528 10.2495 8.58999 10.3848 8.95227 10.3848V10.0002ZM8.21204 9.69254L8.48399 9.42058L5.27012 6.20672L4.99817 6.47867L4.72622 6.75062L7.94008 9.96449L8.21204 9.69254ZM4.99817 6.47867L4.72617 6.20676L1.51328 9.42062L1.78528 9.69254L2.05727 9.96445L5.27016 6.75058L4.99817 6.47867ZM1.78528 9.69254L1.50411 9.43012C1.39292 9.54926 1.22188 9.61548 1.04487 9.61555L1.04504 10.0002L1.04522 10.3848C1.39881 10.3846 1.78669 10.2547 2.06644 9.95496L1.78528 9.69254ZM1.04504 10.0002V9.61555C0.876053 9.61555 0.709392 9.55419 0.575787 9.42058L0.303834 9.69254L0.0318803 9.96449C0.3174 10.25 0.683146 10.3848 1.04504 10.3848V10.0002ZM0.303834 9.69254L0.575814 9.42061C0.320867 9.16561 0.320881 8.73795 0.575787 8.48305L0.303834 8.21109L0.0318803 7.93914C-0.523471 8.49449 -0.52336 9.40914 0.0318532 9.96446L0.303834 9.69254ZM0.303834 8.21109L0.575787 8.48305L3.78868 5.27015L3.51672 4.9982L3.24477 4.72625L0.0318803 7.93914L0.303834 8.21109ZM3.51672 4.9982L3.78868 4.72625L0.575787 1.51336L0.303834 1.78531L0.0318803 2.05726L3.24477 5.27015L3.51672 4.9982ZM0.303834 1.78531L0.575787 1.51336C0.320873 1.25844 0.320862 0.830788 0.57581 0.575795L0.303834 0.303865L0.0318571 0.0319349C-0.523376 0.587263 -0.523471 1.50191 0.0318803 2.05726L0.303834 1.78531ZM0.303834 0.303865L0.575787 0.575819C0.830745 0.320861 1.25837 0.320861 1.51333 0.575819L1.78528 0.303865L2.05723 0.0319116C1.50188 -0.523438 0.58723 -0.523438 0.0318803 0.0319116L0.303834 0.303865ZM1.78528 0.303865L1.51333 0.575819L4.72622 3.78871L4.99817 3.51676L5.27012 3.2448L2.05723 0.0319116L1.78528 0.303865ZM4.99817 3.51676L5.27008 3.78875L8.48395 0.57586L8.21204 0.303865L7.94012 0.0318703L4.72626 3.24476L4.99817 3.51676Z" fill="white" mask="url(#path-1-inside-1_1030_1913)" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className='header-table-filters advanced-table-filters'>
                              <>
                                <div className='left-area'>
                                  <div className='filter-box'>
                                    <div className='searchbox'>
                                      <TextField
                                        fullWidth
                                        hiddenLabel
                                        size="small"
                                        name='search'
                                        variant="standard"
                                        placeholder='Search name, script, or summary'
                                        value={searchText}
                                        onChange={(e) => setSearchText(e['target']['value'])}
                                      />
                                      <i className='icon'>
                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M5.82319 10.6464C3.16455 10.6464 1 8.48183 1 5.82319C1 3.16455 3.16455 1 5.82319 1C8.48183 1 10.6464 3.16455 10.6464 5.82319C10.6464 8.48183 8.48183 10.6464 5.82319 10.6464ZM5.82319 1.70583C3.55041 1.70583 1.70583 3.55511 1.70583 5.82319C1.70583 8.09127 3.55041 9.94055 5.82319 9.94055C8.09597 9.94055 9.94055 8.09127 9.94055 5.82319C9.94055 3.55511 8.09597 1.70583 5.82319 1.70583Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                                          <path d="M13.3182 13.7068C13.2245 13.7069 13.1307 13.6725 13.0566 13.5985L9.10425 9.63167C8.96097 9.48877 8.96065 9.25192 9.10355 9.10864C9.24646 8.96536 9.4833 8.96505 9.62658 9.10795L13.5789 13.0748C13.7222 13.2177 13.7225 13.4546 13.5796 13.5979C13.5057 13.672 13.412 13.7066 13.3182 13.7068Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                                        </svg>
                                      </i>
                                    </div>
                                    {agentTypes.length > 1 && (
                                      <FormControl style={{ marginLeft: '20px' }}>
                                        <Select
                                          displayEmpty
                                          value={selectedType}
                                          onChange={(e) => setSelectedType(e['target']['value'])}
                                          renderValue={(selected) => {
                                            if (selected === 'all') return 'All Types';
                                            return selected;
                                          }}
                                        >
                                          <MenuItem value="all"> <ListItemText primary="All Types" /> </MenuItem>
                                          {agentTypes.map((type) => <MenuItem key={type} value={type}> <ListItemText primary={type} /> </MenuItem>)}
                                        </Select>
                                      </FormControl>
                                    )}
                                  </div>
                                </div>
                                <div className='right-deals-filtes'>
                                  {agentStatuses.length > 1 && (
                                    <div className="filter-deals-owner">
                                      <button className="setting-btn" onClick={handleClick}>
                                        {Object.keys(selectedValues).filter(key => selectedValues[key]).length > 0
                                          ? `${Object.keys(selectedValues).filter(key => selectedValues[key]).length} Status Selected`
                                          : 'All Status'} <i className="fa fa-angle-down"></i>
                                      </button>

                                      <Menu anchorEl={statusDropdownAnchor} open={Boolean(statusDropdownAnchor)} onClose={handleClose}>
                                        {agentStatuses.length === 0 ? (
                                          <MenuItem disabled>
                                            <ListItemText primary="No status options available" />
                                          </MenuItem>
                                        ) : (
                                          agentStatuses.map((status) => (
                                            <MenuItem key={status} onClick={() => toggleValue(status)}>
                                              <Checkbox checked={!!selectedValues[status]} />
                                              <ListItemText primary={status === 'active' ? 'Active' : 'Inactive'} />
                                            </MenuItem>
                                          ))
                                        )}
                                      </Menu>
                                    </div>
                                  )}
                                  {allTags.length > 0 && (
                                    <div className="filter-deals-owner">
                                      <button className="setting-btn" onClick={handleClickTagFilter}>
                                        {selectedTagsList.length > 0
                                          ? `${selectedTagsList.length} Tags Selected`
                                          : 'All Tags'} <i className="fa fa-angle-down"></i>
                                      </button>
                                    </div>
                                  )}
                                  <div className='filter-table-select'>
                                    <button className={`fiters-button ${isTableView ? 'active' : ''}`} onClick={() => setTableView(true)}>
                                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M11.6087 13.2708H2.39199C1.27199 13.2708 0.729492 12.6992 0.729492 11.5325V9.17583C0.729492 8.00333 1.27199 7.4375 2.39199 7.4375H11.6087C12.7287 7.4375 13.2712 8.00917 13.2712 9.17583V11.5325C13.2712 12.6992 12.7287 13.2708 11.6087 13.2708ZM2.39199 8.3125C1.80283 8.3125 1.60449 8.435 1.60449 9.17583V11.5325C1.60449 12.2733 1.80283 12.3958 2.39199 12.3958H11.6087C12.1978 12.3958 12.3962 12.2733 12.3962 11.5325V9.17583C12.3962 8.435 12.1978 8.3125 11.6087 8.3125H2.39199Z" fill="currentColor" />
                                        <path d="M11.6087 6.56234H2.39199C1.27199 6.56234 0.729492 5.99067 0.729492 4.824V2.46734C0.729492 1.29484 1.27199 0.729004 2.39199 0.729004H11.6087C12.7287 0.729004 13.2712 1.30067 13.2712 2.46734V4.824C13.2712 5.99067 12.7287 6.56234 11.6087 6.56234ZM2.39199 1.604C1.80283 1.604 1.60449 1.7265 1.60449 2.46734V4.824C1.60449 5.56484 1.80283 5.68734 2.39199 5.68734H11.6087C12.1978 5.68734 12.3962 5.56484 12.3962 4.824V2.46734C12.3962 1.7265 12.1978 1.604 11.6087 1.604H2.39199V1.604Z" fill="currentColor" />
                                      </svg>
                                    </button>
                                    <button className={`fiters-button ${!isTableView ? 'active' : ''}`} onClick={() => setTableView(false)}>
                                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4.82449 13.2707H2.46783C1.29533 13.2707 0.729492 12.7282 0.729492 11.6082V2.3915C0.729492 1.2715 1.30116 0.729004 2.46783 0.729004H4.82449C5.99699 0.729004 6.56283 1.2715 6.56283 2.3915V11.6082C6.56283 12.7282 5.99116 13.2707 4.82449 13.2707ZM2.46783 1.604C1.72699 1.604 1.60449 1.80234 1.60449 2.3915V11.6082C1.60449 12.1973 1.72699 12.3957 2.46783 12.3957H4.82449C5.56533 12.3957 5.68783 12.1973 5.68783 11.6082V2.3915C5.68783 1.80234 5.56533 1.604 4.82449 1.604H2.46783V1.604Z" fill="currentColor" />
                                        <path d="M11.5325 9.18734H9.17583C8.00333 9.18734 7.4375 8.64484 7.4375 7.52484V2.3915C7.4375 1.2715 8.00917 0.729004 9.17583 0.729004H11.5325C12.705 0.729004 13.2708 1.2715 13.2708 2.3915V7.52484C13.2708 8.64484 12.6992 9.18734 11.5325 9.18734ZM9.17583 1.604C8.435 1.604 8.3125 1.80234 8.3125 2.3915V7.52484C8.3125 8.114 8.435 8.31234 9.17583 8.31234H11.5325C12.2733 8.31234 12.3958 8.114 12.3958 7.52484V2.3915C12.3958 1.80234 12.2733 1.604 11.5325 1.604H9.17583V1.604Z" fill="currentColor" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </>
                            </div>

                          )}
                          {!isTableView
                            ? <BoxView
                              loading={isLoading}
                              agents={filteredContacts}
                              selectedRows={selectedRows}
                              onSelectAgent={handleBoxViewSelect}
                            />
                            : <>
                              <style>{tableStyles}</style>
                              <DataTable
                                on
                                fixedHeader
                                keyField="_id"
                                selectableRows
                                persistTableHead
                                data={dataAgents}
                                paginationPerPage={100}
                                pagination={isPaginated}
                                progressPending={isLoading}
                                clearSelectedRows={toggleCleared}
                                columns={constants['allAgentColumns']}
                                fixedHeaderScrollHeight={headerScrollHeight}
                                onSelectedRowsChange={handleSelectedRowsChange}
                                className={`table-sellagent ${responsiveClass}`}
                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                paginationResetDefaultPage={false}
                                onRowClicked={(row) => history.push(getWorkspacePath(`/agents/${row._id}`, currentWorkspace?._id))}
                                progressComponent={<div style={{ padding: '40px', textAlign: 'center' }}><TableAnimationFiles /></div>}
                                noDataComponent={
                                  <div className='notfound-area'>
                                    <div className='img-box'>
                                      <img alt="..." src={notFoundImg} />
                                    </div>
                                    <p>No agents found</p>
                                  </div>
                                }
                              />
                            </>
                          }
                        </div>
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --------------- Delete Agent--------------- */}
      <Modal isOpen={isDelete} className={`main-modal new-user-modal modal-dialog-centered select-tampltes-modal`}>
        <ModalHeader></ModalHeader>
        <ModalBody>
          <div className='content-area'>
            <div className='delete-modal'>
              <h4>
                <i className='icon'>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z" fill="#FF3B30" fill-opacity="0.15" />
                    <path d="M12 19.5C16.1421 19.5 19.5 16.1421 19.5 12C19.5 7.85786 16.1421 4.5 12 4.5C7.85786 4.5 4.5 7.85786 4.5 12C4.5 16.1421 7.85786 19.5 12 19.5Z" fill="#FF3B30" />
                    <path d="M12 9V12" stroke="#FFE2E0" strokeWidth="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M12 15H12.0083" stroke="#FFE2E0" strokeWidth="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </i>
                Delete Agent
              </h4>
              <p>Are you sure you want to delete this Agent? You can't undo this action afterwards.</p>
              <div className='form-area'>
                <div className='btns-box text-right'>
                  <button className='btn-style-border' onClick={() => setDelete(null)}>Cancel</button>
                  <button className='btn-style-new' onClick={() => { dispatch(deleteAgent(isDelete)); setDelete(null); setSelectedRows([]); }}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Tags Manager Dialog */}
      {isTagsModal && (
        <Dialog
          open={isTagsModal}
          onClose={() => setTagsModal(false)}
          maxWidth="sm"
          fullWidth
          slotProps={{
            paper: {
              sx: {
                borderRadius: '8px',
                minWidth: '260px',
                maxWidth: '320px',
                minHeight: '400px'
              }
            }
          }}
        >
          <TagsManager
            objectType="agents"
            mode="select"
            selectedObjects={selectedRows.map(row => row._id || row)}
            onClose={() => setTagsModal(false)}
            onTagsApplied={() => {
              setTagsModal(false);
              setSelectedRows([]);
              setToggleCleared(prev => !prev);
              dispatch(getAllAgents());
            }}
          />
        </Dialog>
      )}

      {/* Tag Filter Popover */}
      <Popover
        open={Boolean(tagFilterAnchor)}
        anchorEl={tagFilterAnchor}
        onClose={handleCloseTagFilter}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '8px',
              minWidth: '300px',
              maxWidth: '400px',
              minHeight: '300px',
              maxHeight: '400px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }
          }
        }}
      >
        <TagsManager
          objectType="agents"
          mode="filter"
          selectedTags={selectedTagsList}
          onClose={handleCloseTagFilter}
          onTagsChange={handleTagsFilterChange}
          onTagsApplied={() => {
            dispatch(getAllAgents()); // Refresh agents list when tags are updated
          }}
        />
      </Popover>
    </>
  );
};

const tableStyles = `
          .table-sellagent input[type="checkbox"] {
            width: 13px;
          height: 13px;
          cursor: pointer;
          accent-color: #cbd6e2;
          transform: scale(1);
          opacity: 0.45;
  }

          .table-sellagent input[type="checkbox"]:checked {
            opacity: 1;
          accent-color: var(--primary-color);
  }

          .rdt_TableRow {
            transition: background-color 0.15s ease;
  }

          .rdt_TableRow:hover {
            background - color: #f5f5f5;
  }

          .table-container {
            position: relative;
  }
          `;

export default Agents;